/** Formatting people read, in one place, in South African conventions. */

export function hours(seconds: number | null | undefined): string {
  if (!seconds) return '0:00'
  const total = Math.max(0, Math.round(seconds / 60))
  const h = Math.floor(total / 60)
  const m = total % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

/**
 * A running clock: H:MM:SS.
 *
 * Separate from hours() on purpose. A recorded duration is read in minutes — nobody
 * cares that a meeting was two hours and eleven seconds — but a clock that only
 * changes once a minute looks broken, and the timer in the header spent its first
 * minute reading 0:00 while people asked why nothing was running.
 */
export function clock(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.floor(seconds ?? 0))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export function decimalHours(seconds: number | null | undefined): string {
  return ((seconds ?? 0) / 3600).toFixed(2)
}

/** Accepts "1:30", "1.5", "90m", "1h30", "2h" — whatever someone actually types. */
export function parseDuration(input: string): number | null {
  const text = input.trim().toLowerCase()
  if (!text) return null
  let match = text.match(/^(\d+):([0-5]?\d)$/)
  if (match) return Number(match[1]) * 3600 + Number(match[2]) * 60
  match = text.match(/^(\d+(?:[.,]\d+)?)\s*h(?:ours?)?$/)
  if (match) return Math.round(Number(match[1]!.replace(',', '.')) * 3600)
  match = text.match(/^(\d+)\s*h\s*(\d+)\s*m?$/)
  if (match) return Number(match[1]) * 3600 + Number(match[2]) * 60
  match = text.match(/^(\d+)\s*m(?:in(?:utes?)?)?$/)
  if (match) return Number(match[1]) * 60
  match = text.match(/^(\d+(?:[.,]\d+)?)$/)
  if (match) return Math.round(Number(match[1]!.replace(',', '.')) * 3600)
  return null
}

const money0 = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  minimumFractionDigits: 2,
})

export function money(amount: string | number | null | undefined, currency = 'ZAR'): string {
  if (amount === null || amount === undefined) return '—'
  const value = typeof amount === 'string' ? Number(amount) : amount
  if (Number.isNaN(value)) return '—'
  if (currency === 'ZAR') return money0.format(value)
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(value)
}

export function shortDate(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const d = typeof value === 'string' ? new Date(value) : value
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })
}

export function longDate(value: string | Date | null | undefined): string {
  if (!value) return '—'
  const d = typeof value === 'string' ? new Date(value) : value
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function startOfWeek(d: Date): Date {
  const copy = new Date(d)
  const day = (copy.getDay() + 6) % 7 // Monday = 0
  copy.setDate(copy.getDate() - day)
  copy.setHours(0, 0, 0, 0)
  return copy
}

export function addDays(d: Date, n: number): Date {
  const copy = new Date(d)
  copy.setDate(copy.getDate() + n)
  return copy
}

export function weekdayLabel(d: Date): string {
  return d.toLocaleDateString('en-ZA', { weekday: 'short' })
}

export function elapsed(sinceIso: string, now = Date.now()): number {
  return Math.max(0, Math.floor((now - new Date(sinceIso).getTime()) / 1000))
}

/** Cycle time reads badly as "0 days" when work finished the same day. */
export function duration(days: number | null | undefined): string {
  if (days == null) return '—'
  if (days >= 1) return `${days.toFixed(1)} days`
  const h = days * 24
  if (h >= 1) return `${h.toFixed(1)} hours`
  return `${Math.max(1, Math.round(h * 60))} min`
}
